import {useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {AlertTriangle, Power, Search} from 'lucide-react'
import * as API from '../../api'
import {PermissionStatusSelect} from './PermissionStatusSelect'
import {
  getCategoryLabel,
  getPermissionCategory,
  isPermissionActive,
  matchesPermissionStatus,
  type Permission,
  type PermissionStatusFilter,
} from './permissionMetadata'

type Props = {
  permissions: Permission[]
  onClose: () => void
  onSaved: (changes: {id_feature: string | number; is_active: number}[]) => void
}

export function FeatureStatusModal({permissions, onClose, onSaved}: Props) {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<PermissionStatusFilter>('active')
  const [draft, setDraft] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const changes = permissions
    .filter(
      (permission) =>
        draft[String(permission.id_feature)] !== undefined &&
        draft[String(permission.id_feature)] !== isPermissionActive(permission)
    )
    .map((permission) => ({
      id_feature: permission.id_feature,
      is_active: Number(draft[String(permission.id_feature)]),
    }))

  const categories = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase('pt-BR')
    const visible = permissions.filter((permission) => {
      const category = getCategoryLabel(getPermissionCategory(permission.name))
      return (
        matchesPermissionStatus(permission, statusFilter) &&
        `${permission.title} ${permission.name} ${category}`
          .toLocaleLowerCase('pt-BR')
          .includes(normalized)
      )
    })
    return Object.entries(
      visible.reduce((groups, permission) => {
        const category = getCategoryLabel(getPermissionCategory(permission.name))
        ;(groups[category] ||= []).push(permission)
        return groups
      }, {} as Record<string, Permission[]>)
    ).sort(([a], [b]) => a.localeCompare(b, 'pt-BR'))
  }, [permissions, search, statusFilter])

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const result = await API.updatePermissionStatuses(changes)
      if (!result.success) throw new Error(result.error || 'Não foi possível salvar as alterações.')
      onSaved(changes)
    } catch (saveError) {
      setError(
        (saveError as any)?.response?.data?.error ||
          (saveError as Error).message ||
          'Não foi possível salvar as alterações. Tente novamente.'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      show
      onHide={onClose}
      size='xl'
      centered
      scrollable
      backdrop='static'
      keyboard={!saving}
      aria-labelledby='feature-status-title'
    >
      <Modal.Header closeButton={!saving} className='border-gray-200'>
        <Modal.Title
          id='feature-status-title'
          className='flex items-center gap-2 text-lg font-bold'
        >
          <Power className='h-5 w-5 text-blue-600' /> Gerenciar features
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='bg-gray-50'>
        <div className='mb-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'>
          <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0' />
          <p className='m-0'>
            Ao desativar uma feature, as telas e os endpoints vinculados ficam indisponíveis para
            todos os usuários e grupos. Os vínculos são preservados para uma futura reativação.
          </p>
        </div>
        <div className='mb-4 flex flex-wrap items-center gap-3'>
          <label className='relative min-w-[200px] flex-1'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
            <input
              type='search'
              aria-label='Buscar features'
              placeholder='Título, identificador ou categoria...'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className='h-10 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm focus:border-blue-500'
            />
          </label>
          <PermissionStatusSelect value={statusFilter} onChange={setStatusFilter} />
        </div>
        <div className='space-y-4'>
          {categories.map(([category, features]) => (
            <section
              key={category}
              aria-label={category}
              className='overflow-hidden rounded-lg border border-gray-200 bg-white'
            >
              <h3 className='m-0 border-b border-gray-100 bg-gray-50 px-4 py-3 text-sm font-bold text-gray-700'>
                {category}
              </h3>
              <div className='divide-y divide-gray-100'>
                {features
                  .sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'))
                  .map((permission) => {
                    const active =
                      draft[String(permission.id_feature)] ?? isPermissionActive(permission)
                    const changed = active !== isPermissionActive(permission)
                    return (
                      <div
                        key={permission.id_feature}
                        className='flex items-center gap-3 px-4 py-3'
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='text-sm font-semibold text-gray-800'>
                            {permission.title}
                          </div>
                          <div className='mt-0.5 break-all font-mono text-xs text-gray-400'>
                            {permission.name}
                          </div>
                        </div>
                        {changed && (
                          <span className='text-xs text-blue-600'>Alteração pendente</span>
                        )}
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${
                            active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                          }`}
                        >
                          {active ? 'Ativa' : 'Inativa'}
                        </span>
                        <button
                          type='button'
                          role='switch'
                          aria-checked={active}
                          aria-label={permission.title}
                          disabled={saving}
                          onClick={() =>
                            setDraft((current) => ({
                              ...current,
                              [String(permission.id_feature)]: !active,
                            }))
                          }
                          className={`relative h-6 w-11 shrink-0 rounded-full transition focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 ${
                            active ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${
                              active ? 'left-6' : 'left-1'
                            }`}
                          />
                        </button>
                      </div>
                    )
                  })}
              </div>
            </section>
          ))}
          {!categories.length && (
            <p className='py-6 text-center text-sm text-gray-500'>
              Nenhuma feature encontrada para os filtros selecionados.
            </p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className='justify-between'>
        <div className='text-sm text-gray-500'>
          {error ? (
            <span role='alert' className='text-red-600'>
              {error}
            </span>
          ) : (
            `${changes.length} ${
              changes.length === 1 ? 'alteração pendente' : 'alterações pendentes'
            }`
          )}
        </div>
        <div className='flex gap-2'>
          <button
            type='button'
            onClick={onClose}
            disabled={saving}
            className='rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50'
          >
            Cancelar
          </button>
          <button
            type='button'
            onClick={save}
            disabled={saving || !changes.length}
            className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50'
          >
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
